import * as React from "react";
import { Link } from "wouter";
import { ArrowLeft, ChevronLeft } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const backButtonVariants = cva(
  "inline-flex items-center justify-center font-medium text-brand transition-colors hover:bg-brand-tint hover:text-brand-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        ghost: "rounded-md",
        outline: "rounded-md border border-input bg-background",
        pill: "rounded-full border border-border bg-background",
        icon: "rounded-full",
        "icon-bordered": "rounded-full border border-border bg-background hover:shadow-sm",
      },
      size: {
        sm: "gap-1.5 h-8 px-2.5 text-sm",
        default: "gap-2 h-9 px-3 text-sm",
        lg: "gap-2 h-10 px-4 text-sm",
        "icon-xs": "h-7 w-7",
        "icon-sm": "h-9 w-9",
        "icon-md": "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "ghost",
      size: "sm",
    },
  },
);

type BackButtonVariantProps = VariantProps<typeof backButtonVariants>;

export interface BackButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type">,
    BackButtonVariantProps {
  /** When provided, renders as a wouter <Link> instead of a <button>. */
  href?: string;
  /** Which icon glyph to use. Default `chevron`. */
  icon?: "chevron" | "arrow";
  /** Size class applied to the icon. Default `h-4 w-4`. */
  iconClassName?: string;
  /** Render the icon only (label hidden). Auto-set when no children are provided and an icon size is used. */
  iconOnly?: boolean;
  type?: "button" | "submit" | "reset";
}

function isIconSize(size: BackButtonVariantProps["size"]): boolean {
  return size === "icon-xs" || size === "icon-sm" || size === "icon-md";
}

/**
 * Brand-styled back navigation control.
 *
 * Centralizes the `text-brand hover:bg-brand-tint hover:text-brand-hover`
 * styling used across back buttons / back links. Supports icon-only,
 * icon+label, and link forms.
 *
 * Examples:
 * ```tsx
 * <BackButton onClick={onBack}>Back to list</BackButton>
 * <BackButton variant="icon" iconOnly aria-label="Back" onClick={back} />
 * <BackButton href="/dashboard/bookings" variant="pill" icon="arrow">
 *   Back to bookings
 * </BackButton>
 * ```
 */
export function BackButton({
  href,
  variant,
  size,
  icon = "chevron",
  iconClassName,
  iconOnly,
  className,
  children,
  type = "button",
  ...props
}: BackButtonProps) {
  const Icon = icon === "arrow" ? ArrowLeft : ChevronLeft;
  const resolvedIconOnly = iconOnly ?? (children == null && isIconSize(size));
  const classes = cn(backButtonVariants({ variant, size }), className);

  const content = (
    <>
      <Icon className={cn("h-4 w-4", iconClassName)} aria-hidden="true" />
      {!resolvedIconOnly && children != null ? <span>{children}</span> : null}
    </>
  );

  if (href != null) {
    return (
      <Link
        href={href}
        className={classes}
        {...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {content}
      </Link>
    );
  }

  return (
    <button type={type} className={classes} {...props}>
      {content}
    </button>
  );
}

export default BackButton;
